import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './ReceiptModelsTab.css';

const BANKS = ['BANRESERVAS', 'BANCO POPULAR', 'BANCO BHD LEON'];
const TYPES = [
  { key: 'transferencia', label: 'Transferencia' },
  { key: 'deposito', label: 'Depósito' },
  { key: 'subagente', label: 'Subagente/Agente' },
  { key: 'cajero', label: 'Cajero' }
];

const BANK_COLORS = {
  'BANRESERVAS': '#0047ab',
  'BANCO POPULAR': '#d4002a',
  'BANCO BHD LEON': '#f5a800'
};

const ReceiptModelsTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [deleting, setDeleting] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [preview, setPreview] = useState(null); // { url, label }
  const fileInputRefs = useRef({});

  useEffect(() => {
    loadTemplates();
  }, []);

  const showMsg = (type, text, ms = 4000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), ms);
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/receipts/templates');
      if (res.data.success) setTemplates(res.data.templates || []);
    } catch (err) {
      showMsg('error', 'Error al cargar plantillas: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (bank, type, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showMsg('error', 'Solo se permiten imágenes (JPG, PNG, WEBP)');
      return;
    }
    const key = `${bank}_${type}`;
    try {
      setUploading(prev => ({ ...prev, [key]: true }));
      const formData = new FormData();
      formData.append('image', file);
      formData.append('bank', bank);
      formData.append('type', type);
      formData.append('label', `${bank} - ${type}`);
      const res = await axios.post('/api/receipts/templates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        setTemplates(prev => [...prev, res.data.template]);
        showMsg('success', `✅ Plantilla de ${type} para ${bank} subida correctamente`);
      } else {
        showMsg('error', res.data.error || 'Error al subir plantilla');
      }
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Error al subir plantilla');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
      // Limpiar input para permitir subir el mismo archivo de nuevo
      const ref = fileInputRefs.current[key];
      if (ref) ref.value = '';
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`¿Eliminar esta plantilla de ${tpl.type} para ${tpl.bank}?`)) return;
    try {
      setDeleting(prev => ({ ...prev, [tpl._id]: true }));
      await axios.delete(`/api/receipts/templates/${tpl._id}`);
      setTemplates(prev => prev.filter(t => t._id !== tpl._id));
      showMsg('success', 'Plantilla eliminada');
    } catch (err) {
      showMsg('error', 'Error al eliminar plantilla');
    } finally {
      setDeleting(prev => ({ ...prev, [tpl._id]: false }));
    }
  };

  const handleToggle = async (tpl) => {
    try {
      const res = await axios.patch(`/api/receipts/templates/${tpl._id}`, { active: !tpl.active });
      if (res.data.success) {
        setTemplates(prev => prev.map(t => t._id === tpl._id ? res.data.template : t));
      }
    } catch (err) {
      showMsg('error', 'Error al actualizar plantilla');
    }
  };

  const getImageUrl = (tpl) => {
    // Usar ruta de imagen directa si está disponible
    if (tpl.imagePath) return tpl.imagePath;
    return `/api/receipts/templates/${tpl._id}/image`;
  };

  const templatesFor = (bank, type) =>
    templates.filter(t => t.bank === bank && t.type === type);

  if (loading) {
    return (
      <div className="receipt-models-loading">
        <div className="loading-spinner">⏳</div>
        <p>Cargando plantillas de recibos...</p>
      </div>
    );
  }

  return (
    <div className="receipt-models-tab">
      {/* Mensaje de estado */}
      {message.text && (
        <div className={`receipt-models-message ${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
        </div>
      )}

      {/* Cabecera explicativa */}
      <div className="receipt-models-info">
        <h3>📋 Modelos de Recibos Bancarios</h3>
        <p className="config-help">
          Sube imágenes de recibos reales como plantillas visuales. El sistema de IA usará estos ejemplos
          para identificar el banco por el diseño y colores, incluso cuando el número de cuenta no aparezca.
          Puedes subir <strong>múltiples ejemplos</strong> por banco y tipo.
        </p>
        <div className="receipt-models-stats">
          <span>Total plantillas: <strong>{templates.length}</strong></span>
          <span style={{marginLeft: 16}}>Activas: <strong>{templates.filter(t => t.active).length}</strong></span>
          <button className="btn-refresh-small" onClick={loadTemplates} title="Recargar">🔄 Actualizar</button>
        </div>
      </div>

      {/* Grid por banco */}
      {BANKS.map(bank => (
        <div key={bank} className="receipt-bank-section" style={{ borderLeftColor: BANK_COLORS[bank] }}>
          <h4 className="bank-title" style={{ color: BANK_COLORS[bank] }}>
            🏦 {bank}
          </h4>

          <div className="receipt-types-grid">
            {TYPES.map(type => {
              const key = `${bank}_${type.key}`;
              const tpls = templatesFor(bank, type.key);
              const isUploading = uploading[key];

              return (
                <div key={type.key} className="receipt-type-card">
                  <div className="receipt-type-header">
                    <span className="receipt-type-label">{type.label}</span>
                    <span className="receipt-type-count">
                      {tpls.length > 0 ? `${tpls.length} plantilla${tpls.length > 1 ? 's' : ''}` : 'Sin plantillas'}
                    </span>
                  </div>

                  {/* Miniaturas de plantillas existentes */}
                  {tpls.length > 0 && (
                    <div className="receipt-thumbnails">
                      {tpls.map(tpl => (
                        <div key={tpl._id} className={`receipt-thumbnail ${!tpl.active ? 'inactive' : ''}`}>
                          <img
                            src={getImageUrl(tpl)}
                            alt={tpl.label}
                            className="thumbnail-img"
                            onClick={() => setPreview({ url: getImageUrl(tpl), label: tpl.label || `${tpl.bank} ${tpl.type}` })}
                            title="Ver imagen completa"
                          />
                          <div className="thumbnail-actions">
                            <button
                              className={`btn-toggle ${tpl.active ? 'active' : 'inactive'}`}
                              onClick={() => handleToggle(tpl)}
                              title={tpl.active ? 'Desactivar' : 'Activar'}
                            >
                              {tpl.active ? '✅' : '⏸️'}
                            </button>
                            <button
                              className="btn-delete-thumb"
                              onClick={() => handleDelete(tpl)}
                              disabled={deleting[tpl._id]}
                              title="Eliminar"
                            >
                              {deleting[tpl._id] ? '...' : '🗑️'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Botón agregar nueva plantilla */}
                  <label className={`receipt-upload-area ${tpls.length > 0 ? 'compact' : ''}`}>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      ref={el => (fileInputRefs.current[key] = el)}
                      onChange={e => handleUpload(bank, type.key, e.target.files[0])}
                      disabled={isUploading}
                    />
                    <div className="receipt-upload-placeholder">
                      {isUploading ? (
                        <>
                          <span className="upload-icon">⏳</span>
                          <span>Subiendo...</span>
                        </>
                      ) : (
                        <>
                          <span className="upload-icon">📤</span>
                          <span>{tpls.length > 0 ? 'Agregar otro ejemplo' : 'Subir modelo'}</span>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Modal de vista previa */}
      {preview && (
        <div className="receipt-preview-overlay" onClick={() => setPreview(null)}>
          <div className="receipt-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <span>{preview.label}</span>
              <button onClick={() => setPreview(null)}>✕</button>
            </div>
            <img src={preview.url} alt={preview.label} className="receipt-preview-img" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptModelsTab;







